import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Users, UserCheck, UserX, GraduationCap, Calendar, TrendingUp, AlertTriangle,
  Plus, FileText, Wrench, Briefcase, Award, Clock, Sparkles, Bell, Loader2,
  ArrowUpRight, Activity, RefreshCw, Banknote, ArrowRight
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip as RTooltip, CartesianGrid, Legend
} from "recharts";
import { cn } from "@/lib/utils";

const API = "/api";

// Brand palette — indigo / emerald / amber / rose with neutral slate
const COLORS = {
  primary: "#4F46E5",      // indigo-600
  primarySoft: "#E0E7FF",
  success: "#10B981",      // emerald-500
  successSoft: "#D1FAE5",
  warning: "#F59E0B",      // amber-500
  warningSoft: "#FEF3C7",
  danger:  "#E11D48",      // rose-600
  dangerSoft: "#FFE4E6",
  slate:   "#64748B",
};

const PIE_COLORS = [COLORS.primary, COLORS.success, COLORS.warning, COLORS.danger, COLORS.slate, "#8B5CF6", "#0EA5E9"];

function KPI({ label, value, icon: Icon, tone = "primary", testid, hint }) {
  const toneClass = {
    primary: "border-indigo-200 bg-white",
    success: "border-emerald-200 bg-white",
    warning: "border-amber-200 bg-white",
    danger: "border-rose-200 bg-white",
    slate: "border-slate-200 bg-white",
  }[tone];
  const iconBg = {
    primary: "bg-indigo-50 text-indigo-600",
    success: "bg-emerald-50 text-emerald-600",
    warning: "bg-amber-50 text-amber-600",
    danger: "bg-rose-50 text-rose-600",
    slate: "bg-slate-50 text-slate-600",
  }[tone];
  return (
    <Card className={cn("transition-shadow hover:shadow-md", toneClass)} data-testid={testid}>
      <CardContent className="pt-5 pb-5">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
            <p className="text-2xl lg:text-3xl font-bold text-slate-900 mt-1 truncate">{value}</p>
            {hint && <p className="text-xs text-slate-400 mt-1 truncate">{hint}</p>}
          </div>
          <div className={cn("p-2.5 rounded-lg shrink-0", iconBg)}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function QuickAction({ to, label, icon: Icon, accent = "indigo" }) {
  const cls = {
    indigo: "border-indigo-200 hover:border-indigo-300 hover:bg-indigo-50/40 text-indigo-700",
    emerald: "border-emerald-200 hover:border-emerald-300 hover:bg-emerald-50/40 text-emerald-700",
    amber: "border-amber-200 hover:border-amber-300 hover:bg-amber-50/40 text-amber-700",
    rose: "border-rose-200 hover:border-rose-300 hover:bg-rose-50/40 text-rose-700",
    slate: "border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700",
  }[accent] || "border-slate-200";
  return (
    <Link
      to={to}
      className={cn(
        "rounded-lg border bg-white p-3 flex items-center gap-2.5 transition text-sm font-medium",
        cls
      )}
      data-testid={`quick-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <Icon className="w-4 h-4 shrink-0" />
      <span className="truncate">{label}</span>
    </Link>
  );
}

const NATIONALITY_META = {
  korean:    { flag: "🇰🇷", accent: "from-rose-50 to-white border-rose-200" },
  brazilian: { flag: "🇧🇷", accent: "from-emerald-50 to-white border-emerald-200" },
  turkish:   { flag: "🇹🇷", accent: "from-red-50 to-white border-red-200" },
};

function PayrollCutoffSummary({ getAuthHeader, navigate }) {
  // Default to the current cut-off period based on today's date (Asia/Manila roughly = local)
  const computeDefault = () => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const day = now.getDate();
    return { month: `${yyyy}-${mm}`, period: day <= 15 ? "first" : "second" };
  };
  const [{ month, period }, setSel] = useState(computeDefault);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const periodRange = useCallback((m, p) => {
    const [y, mo] = m.split("-").map(Number);
    const lastDay = new Date(y, mo, 0).getDate(); // day 0 of next month = last day of this month
    if (p === "first") return { start: `${m}-01`, end: `${m}-15`, label: `${m}-01 → ${m}-15` };
    return { start: `${m}-16`, end: `${m}-${String(lastDay).padStart(2, "0")}`,
             label: `${m}-16 → ${m}-${String(lastDay).padStart(2, "0")}` };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { start, end } = periodRange(month, period);
    try {
      const r = await axios.get(`${API}/dashboard/payroll-summary?start_date=${start}&end_date=${end}`, {
        headers: getAuthHeader(),
      });
      setData(r.data);
    } catch (e) {
      console.error("[payroll-summary] fetch failed", e);
      setError(e?.response?.data?.detail || "Failed to load payroll summary");
    } finally {
      setLoading(false);
    }
  }, [month, period, periodRange, getAuthHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const monthOptions = useCallback(() => {
    const opts = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleString("en-US", { month: "long", year: "numeric" });
      opts.push({ value, label });
    }
    return opts;
  }, []);

  const { label: rangeLabel } = periodRange(month, period);
  const teachers = (data?.teachers || [])
    .filter((t) => t.total_classes > 0 || t.total_pay > 0)
    .sort((a, b) => (b.total_pay || 0) - (a.total_pay || 0));
  const grandTotal = data?.grand_total || 0;
  const totalClasses = teachers.reduce((sum, t) => sum + (t.total_classes || 0), 0);

  return (
    <section data-testid="payroll-cutoff-summary">
      <div className="flex items-end justify-between flex-wrap gap-2 mb-3">
        <h2 className="text-base font-semibold text-slate-700 flex items-center gap-2">
          <Banknote className="w-4 h-4 text-emerald-600" /> Payroll Cut-Off Summary
        </h2>
        <Button
          size="sm"
          variant="outline"
          className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
          onClick={() => navigate("/teachers")}
          data-testid="payroll-view-full-btn"
        >
          View Full Payroll <ArrowRight className="w-3.5 h-3.5 ml-1" />
        </Button>
      </div>

      <Card className="border-emerald-200">
        <CardContent className="pt-5 pb-5 space-y-4">
          {/* Selector row */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <select
                value={month}
                onChange={(e) => setSel({ month: e.target.value, period })}
                className="border border-slate-300 rounded-md text-sm px-3 py-1.5 bg-white"
                data-testid="payroll-month-select"
              >
                {monthOptions().map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <div className="flex rounded-md border border-slate-200 overflow-hidden" data-testid="payroll-period-toggle">
                <button
                  type="button"
                  onClick={() => setSel({ month, period: "first" })}
                  className={cn(
                    "px-3 py-1.5 text-xs font-medium transition-colors",
                    period === "first" ? "bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
                  )}
                  data-testid="payroll-period-first"
                >
                  1st – 15th
                </button>
                <button
                  type="button"
                  onClick={() => setSel({ month, period: "second" })}
                  className={cn(
                    "px-3 py-1.5 text-xs font-medium transition-colors border-l border-slate-200",
                    period === "second" ? "bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
                  )}
                  data-testid="payroll-period-second"
                >
                  16th – End of Month
                </button>
              </div>
              <Badge variant="outline" className="text-[11px] text-slate-500">{rangeLabel}</Badge>
            </div>
            <Button size="sm" variant="ghost" onClick={fetchData} disabled={loading} data-testid="payroll-refresh-btn">
              <RefreshCw className={cn("w-3.5 h-3.5 mr-1.5", loading && "animate-spin")} /> Refresh
            </Button>
          </div>

          {/* Grand total */}
          <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 px-5 py-4 flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-emerald-700/80">Total Amount to Release</p>
              <p className="text-3xl font-bold text-emerald-700" data-testid="payroll-grand-total">
                {loading ? <Loader2 className="w-6 h-6 animate-spin inline" /> : `₱${grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              </p>
            </div>
            <div className="text-right text-xs text-slate-500 space-y-1">
              <p><span className="font-medium text-slate-700">{teachers.length}</span> tutor{teachers.length === 1 ? "" : "s"} with activity</p>
              <p><span className="font-medium text-slate-700">{totalClasses}</span> classes counted</p>
            </div>
          </div>

          {/* Per-tutor breakdown */}
          {error ? (
            <p className="text-sm text-rose-700">{error}</p>
          ) : loading && !data ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : teachers.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No payroll activity recorded in this cut-off yet.</p>
          ) : (
            <div className="rounded-md border border-slate-200 divide-y divide-slate-100">
              <div className="grid grid-cols-12 px-3 py-2 text-[11px] uppercase tracking-wide text-slate-500 bg-slate-50">
                <div className="col-span-1">#</div>
                <div className="col-span-6">Tutor</div>
                <div className="col-span-2 text-right">Classes</div>
                <div className="col-span-3 text-right">Amount Due</div>
              </div>
              {teachers.map((t, i) => (
                <Link
                  key={t.teacher_id}
                  to={`/salary-tracker/${t.teacher_id}`}
                  className="grid grid-cols-12 px-3 py-2 text-sm items-center hover:bg-emerald-50/40 transition"
                  data-testid={`payroll-row-${t.teacher_id}`}
                >
                  <div className="col-span-1 text-xs text-slate-400">{i + 1}.</div>
                  <div className="col-span-6 text-slate-800 truncate">{t.teacher_name}</div>
                  <div className="col-span-2 text-right">
                    <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs">
                      {t.total_classes}
                    </Badge>
                  </div>
                  <div className="col-span-3 text-right font-semibold text-emerald-700">
                    ₱{(t.total_pay || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

function NationalityCard({ nat }) {
  const meta = NATIONALITY_META[nat.key] || { flag: "🌐", accent: "from-slate-50 to-white border-slate-200" };
  return (
    <Card
      className={cn("bg-gradient-to-br border", meta.accent)}
      data-testid={`nationality-card-${nat.key}`}
    >
      <CardContent className="pt-5 pb-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-3xl leading-none" aria-hidden>{meta.flag}</span>
            <span className="text-sm font-semibold text-slate-800">{nat.label} Students</span>
          </div>
          <Badge variant="outline" className="bg-white text-slate-700 border-slate-300 text-xs">
            {nat.total === 0 ? "none yet" : `${nat.total} total`}
          </Badge>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-white/80 rounded-md px-3 py-2 border border-slate-200">
            <p className="text-[10px] uppercase text-slate-500 tracking-wide">Total</p>
            <p className="text-2xl font-bold text-slate-900" data-testid={`nationality-${nat.key}-total`}>{nat.total}</p>
          </div>
          <div className="bg-white/80 rounded-md px-3 py-2 border border-emerald-200">
            <p className="text-[10px] uppercase text-emerald-600 tracking-wide">Active</p>
            <p className="text-2xl font-bold text-emerald-700" data-testid={`nationality-${nat.key}-active`}>{nat.active}</p>
          </div>
        </div>
        {nat.inactive > 0 && (
          <p className="text-[11px] text-slate-500 mt-2">
            <span className="font-medium">{nat.inactive}</span> inactive
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function AlertChip({ count, label, to, tone = "rose" }) {
  if (!count) return null;
  const cls = {
    rose: "bg-rose-50 text-rose-800 border-rose-200 hover:bg-rose-100",
    amber: "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100",
    indigo: "bg-indigo-50 text-indigo-800 border-indigo-200 hover:bg-indigo-100",
  }[tone];
  return (
    <Link
      to={to}
      className={cn("rounded-md border px-3 py-2 text-xs font-medium inline-flex items-center gap-2 transition", cls)}
    >
      <Bell className="w-3.5 h-3.5" />
      <span><strong>{count}</strong> {label}</span>
      <ArrowUpRight className="w-3 h-3 opacity-60" />
    </Link>
  );
}

export default function Dashboard() {
  const { getAuthHeader, user } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/dashboard-analytics`, { headers: getAuthHeader() });
      setAnalytics(r.data);
    } catch (e) {
      // Silently fall back — keeps existing functionality untouched even if endpoint fails
      console.error("[dashboard] analytics fetch failed", e);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 60_000);
    return () => clearInterval(t);
  }, [fetchAll]);

  if (loading && !analytics) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <Card className="border-rose-200 bg-rose-50">
        <CardContent className="py-8 text-center text-rose-800">
          Couldn&apos;t load analytics. Please refresh.
        </CardContent>
      </Card>
    );
  }

  const { overview, bookings, tutors, students, trends, alerts } = analytics;

  // Student status donut data
  const statusDonut = [
    { name: "Active", value: overview.active_students, color: COLORS.success },
    { name: "Inactive", value: overview.inactive_students, color: COLORS.slate },
    { name: "Trial", value: overview.trial_students, color: COLORS.warning },
  ].filter((d) => d.value > 0);

  return (
    <div className="space-y-6" data-testid="admin-dashboard-page">
      {/* HEADER */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="dashboard-heading">
            Welcome back, {user?.name?.split(" ")[0] || "Admin"} 👋
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Operational analytics across students, tutors and bookings. Updated every minute.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            Last refresh: {new Date(analytics.generated_at).toLocaleTimeString()}
          </Badge>
          <Button variant="outline" size="sm" onClick={fetchAll} data-testid="dashboard-refresh-btn">
            <RefreshCw className={cn("w-4 h-4 mr-1.5", loading && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </div>

      {/* ALERTS STRIP */}
      <div className="flex flex-wrap gap-2" data-testid="alerts-strip">
        <AlertChip count={alerts.untagged_classes} label="untagged classes" to="/admin/untagged-alerts" tone="rose" />
        <AlertChip count={alerts.tutors_missing_zoom_count} label="tutors missing Zoom link" to="/admin-tutor-profiles" tone="rose" />
        <AlertChip count={alerts.low_credit_count} label="low-credit students" to="/students?filter=low-credit" tone="amber" />
        <AlertChip count={alerts.near_expiration_count} label="near expiration" to="/students?filter=expiring" tone="amber" />
        <AlertChip count={alerts.tutors_with_untagged_count} label="tutors with untagged" to="/admin/untagged-alerts" tone="rose" />
        <AlertChip count={alerts.trial_waiting_count} label="trial waiting" to="/trials" tone="indigo" />
        {alerts.untagged_classes + alerts.low_credit_count + alerts.near_expiration_count === 0 && (
          <Badge variant="outline" className="text-emerald-700 bg-emerald-50 border-emerald-200">
            ✅ No urgent alerts. Everything looks good.
          </Badge>
        )}
      </div>

      {/* QUICK ACTIONS */}
      <Card className="border-slate-200">
        <CardContent className="pt-5 pb-5">
          <p className="text-xs uppercase text-slate-500 mb-3 tracking-wide font-medium">Quick Actions</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
            <QuickAction to="/students" label="Add Student" icon={Plus} accent="indigo" />
            <QuickAction to="/admin-tutor-profiles" label="Add Tutor" icon={GraduationCap} accent="emerald" />
            <QuickAction to="/trials" label="Create Trial Booking" icon={Sparkles} accent="amber" />
            <QuickAction to="/admin/untagged-alerts" label="View Untagged Classes" icon={AlertTriangle} accent="rose" />
            <QuickAction to="/teachers" label="Open Payroll" icon={FileText} accent="slate" />
            <QuickAction to="/admin/schedule-repair" label="Open Schedule Repair" icon={Wrench} accent="indigo" />
            <QuickAction to="/recruitment" label="Open Recruitment" icon={Briefcase} accent="emerald" />
            <QuickAction to="/schedule" label="Open Schedule" icon={Calendar} accent="slate" />
          </div>
        </CardContent>
      </Card>

      {/* BUSINESS OVERVIEW */}
      <section>
        <h2 className="text-base font-semibold text-slate-700 mb-3">Business Overview</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <KPI label="Total Students" value={overview.total_students} icon={Users} tone="primary" testid="kpi-total-students" />
          <KPI label="Active Students" value={overview.active_students} icon={UserCheck} tone="success" testid="kpi-active-students" />
          <KPI label="Inactive Students" value={overview.inactive_students} icon={UserX} tone="slate" testid="kpi-inactive-students" />
          <KPI label="Total Tutors" value={overview.total_tutors} icon={GraduationCap} tone="primary" testid="kpi-total-tutors" />
          <KPI label="Trial Students" value={overview.trial_students} icon={Sparkles} tone="warning" testid="kpi-trial-students" />
          <KPI label="Classes Conducted" value={overview.total_classes_conducted} icon={Award} tone="success" testid="kpi-classes-conducted" />
          <KPI label="Credits Used" value={overview.total_credits_used} icon={TrendingUp} tone="slate" testid="kpi-credits-used" />
          <KPI label="Credits Remaining" value={overview.total_credits_remaining} icon={Activity} tone="primary" testid="kpi-credits-remaining" />
          <KPI label="New This Month" value={overview.new_enrollments_this_month} icon={Plus} tone="success" testid="kpi-new-enrollments" />
          <KPI label="Renewals This Month" value={overview.renewals_this_month} icon={RefreshCw} tone="success" testid="kpi-renewals" />
        </div>
      </section>

      {/* STUDENT NATIONALITY BREAKDOWN */}
      {overview.nationality_breakdown?.length > 0 && (
        <section data-testid="nationality-breakdown-section">
          <h2 className="text-base font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <span>🌍</span> Student Nationality Breakdown
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {overview.nationality_breakdown.map((nat) => (
              <NationalityCard key={nat.key} nat={nat} />
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-2">
            Each student is counted exactly once. The <code className="bg-slate-100 px-1 py-0.5 rounded">nationality</code> field
            takes priority; otherwise the name is checked for Korean/Brazilian keywords, with the rest defaulting to Turkish.
            Students tagged with any other nationality (e.g. <code className="bg-slate-100 px-1 py-0.5 rounded">Japanese</code>) are excluded from all three buckets.
          </p>
        </section>
      )}

      {/* BOOKING ANALYTICS */}
      <section>
        <h2 className="text-base font-semibold text-slate-700 mb-3">Booking Analytics</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <KPI label="Booked Today" value={bookings.booked_today} icon={Calendar} tone="primary" testid="kpi-booked-today" />
          <KPI label="Booked This Week" value={bookings.booked_this_week} icon={Calendar} tone="primary" testid="kpi-booked-week" />
          <KPI label="Booked This Month" value={bookings.booked_this_month} icon={Calendar} tone="primary" testid="kpi-booked-month" />
          <KPI label="Upcoming Today" value={bookings.upcoming_today} icon={Clock} tone="warning" testid="kpi-upcoming-today" />
          <KPI label="Upcoming Tomorrow" value={bookings.upcoming_tomorrow} icon={Clock} tone="warning" testid="kpi-upcoming-tomorrow" />
          <KPI label="Student Cancel %" value={`${bookings.student_cancel_rate_pct}%`} icon={UserX} tone="danger" testid="kpi-student-cancel-rate" hint="this month" />
          <KPI label="Teacher Cancel %" value={`${bookings.teacher_cancel_rate_pct}%`} icon={UserX} tone="danger" testid="kpi-teacher-cancel-rate" hint="this month" />
          <KPI label="Tutors Clocked In" value={tutors.clocked_in_count} icon={UserCheck} tone="success" testid="kpi-clocked-in" />
          <KPI label="Tutors Clocked Out" value={tutors.clocked_out_count} icon={UserX} tone="slate" testid="kpi-clocked-out" />
          <KPI label="Untagged Classes" value={alerts.untagged_classes} icon={AlertTriangle} tone={alerts.untagged_classes > 0 ? "danger" : "success"} testid="kpi-untagged" />
        </div>
      </section>

      {/* PAYROLL CUT-OFF SUMMARY */}
      <PayrollCutoffSummary getAuthHeader={getAuthHeader} navigate={navigate} />

      {/* CHARTS GRID */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Booking trend */}
        <Card className="lg:col-span-2" data-testid="chart-booking-trend">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Class Booking Trend</CardTitle>
            <p className="text-xs text-slate-500">Last 8 weeks (week-start)</p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={trends.booking_trend} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="g-book" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.5}/>
                    <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="g-trial" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.warning} stopOpacity={0.5}/>
                    <stop offset="95%" stopColor={COLORS.warning} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="week" tick={{ fontSize: 11, fill: COLORS.slate }} />
                <YAxis tick={{ fontSize: 11, fill: COLORS.slate }} allowDecimals={false} />
                <RTooltip />
                <Legend />
                <Area type="monotone" dataKey="bookings" name="Bookings" stroke={COLORS.primary} fillOpacity={1} fill="url(#g-book)" />
                <Area type="monotone" dataKey="trials" name="Trials" stroke={COLORS.warning} fillOpacity={1} fill="url(#g-trial)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Student status donut */}
        <Card data-testid="chart-student-status">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Student Status</CardTitle>
            <p className="text-xs text-slate-500">Active vs Inactive vs Trial</p>
          </CardHeader>
          <CardContent>
            {statusDonut.length === 0 ? (
              <div className="text-sm text-slate-400 py-12 text-center">No data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={statusDonut} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2}>
                    {statusDonut.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <RTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Student growth */}
        <Card data-testid="chart-student-growth">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Student Growth</CardTitle>
            <p className="text-xs text-slate-500">Cumulative total — last 6 months</p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={trends.student_growth} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: COLORS.slate }} />
                <YAxis tick={{ fontSize: 11, fill: COLORS.slate }} allowDecimals={false} />
                <RTooltip />
                <Line type="monotone" dataKey="total" stroke={COLORS.success} strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Tutor distribution */}
        <Card className="lg:col-span-2" data-testid="chart-tutor-distribution">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Tutor Booking Distribution</CardTitle>
            <p className="text-xs text-slate-500">Total non-trial classes per tutor</p>
          </CardHeader>
          <CardContent>
            {tutors.most_booked.length === 0 ? (
              <div className="text-sm text-slate-400 py-12 text-center">No bookings yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={tutors.most_booked} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: COLORS.slate }} angle={-20} height={50} textAnchor="end" />
                  <YAxis tick={{ fontSize: 11, fill: COLORS.slate }} allowDecimals={false} />
                  <RTooltip />
                  <Bar dataKey="count" name="Classes" fill={COLORS.primary} radius={[6, 6, 0, 0]}>
                    {tutors.most_booked.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* TABLES — Tutor + Student analytics */}
      <Tabs defaultValue="tutors" className="space-y-4">
        <TabsList>
          <TabsTrigger value="tutors" data-testid="tab-tutors">Tutor Analytics</TabsTrigger>
          <TabsTrigger value="students" data-testid="tab-students">Student Analytics</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">Monitoring</TabsTrigger>
        </TabsList>

        <TabsContent value="tutors" className="grid lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Most Booked Tutors</CardTitle>
            </CardHeader>
            <CardContent>
              {tutors.most_booked.length === 0
                ? <p className="text-sm text-slate-400">No data</p>
                : tutors.most_booked.slice(0, 8).map((t, i) => (
                  <div key={t.id} className="flex items-center justify-between py-1.5 text-sm border-b last:border-0">
                    <span className="flex items-center gap-2">
                      <span className="w-5 text-xs text-slate-400">{i + 1}.</span>
                      <span className="text-slate-700">{t.name}</span>
                    </span>
                    <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">{t.count}</Badge>
                  </div>
                ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Tutors with Untagged Classes</CardTitle>
            </CardHeader>
            <CardContent>
              {tutors.tutors_with_untagged.length === 0
                ? <p className="text-sm text-emerald-600">✅ All tutors are up to date</p>
                : tutors.tutors_with_untagged.slice(0, 8).map((t) => (
                  <div key={t.id} className="flex items-center justify-between py-1.5 text-sm border-b last:border-0">
                    <span className="text-slate-700">{t.name}</span>
                    <button
                      type="button"
                      onClick={() => navigate(`/schedule?tutor_id=${encodeURIComponent(t.id)}`)}
                      className="text-rose-700 hover:underline text-xs"
                    >
                      <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">{t.count} pending</Badge>
                    </button>
                  </div>
                ))}
            </CardContent>
          </Card>
          <Card className="lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Tutors Currently Clocked In</CardTitle>
            </CardHeader>
            <CardContent>
              {tutors.clocked_in.length === 0
                ? <p className="text-sm text-slate-400">No tutors currently clocked in</p>
                : (
                  <div className="flex flex-wrap gap-2">
                    {tutors.clocked_in.map((t) => (
                      <Badge key={t.id} variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                        🟢 {t.name}
                      </Badge>
                    ))}
                  </div>
                )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="students" className="grid lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Most Active Students (last 60d)</CardTitle>
            </CardHeader>
            <CardContent>
              {students.most_active.length === 0
                ? <p className="text-sm text-slate-400">No data</p>
                : students.most_active.slice(0, 8).map((s, i) => (
                  <Link key={s.id} to={`/students/${s.id}`}
                    className="flex items-center justify-between py-1.5 text-sm border-b last:border-0 hover:bg-slate-50 px-2 rounded">
                    <span className="flex items-center gap-2">
                      <span className="w-5 text-xs text-slate-400">{i + 1}.</span>
                      <span className="text-slate-700">{s.name}</span>
                    </span>
                    <div className="text-xs text-slate-500">
                      <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 mr-1">{s.total} total</Badge>
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{s.attendance_rate}% attend.</Badge>
                    </div>
                  </Link>
                ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Students with Low Credits</CardTitle>
            </CardHeader>
            <CardContent>
              {students.low_credit.length === 0
                ? <p className="text-sm text-emerald-600">✅ No low-credit students</p>
                : students.low_credit.slice(0, 8).map((s) => (
                  <Link key={s.id} to={`/students/${s.id}`}
                    className="flex items-center justify-between py-1.5 text-sm border-b last:border-0 hover:bg-slate-50 px-2 rounded">
                    <span className="text-slate-700">{s.name}</span>
                    <Badge variant="outline" className={cn(
                      "border-amber-200",
                      s.remaining === 0 ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700"
                    )}>
                      {s.remaining} credits
                    </Badge>
                  </Link>
                ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Highest Attendance</CardTitle>
            </CardHeader>
            <CardContent>
              {students.high_attendance.length === 0
                ? <p className="text-sm text-slate-400">No data yet</p>
                : students.high_attendance.map((s) => (
                  <Link key={s.id} to={`/students/${s.id}`}
                    className="flex items-center justify-between py-1.5 text-sm border-b last:border-0 hover:bg-slate-50 px-2 rounded">
                    <span className="text-slate-700">{s.name}</span>
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                      {s.attendance_rate}% ({s.attended}/{s.total})
                    </Badge>
                  </Link>
                ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Frequent Cancellers</CardTitle>
            </CardHeader>
            <CardContent>
              {students.frequent_cancellers.length === 0
                ? <p className="text-sm text-emerald-600">✅ No frequent cancellers</p>
                : students.frequent_cancellers.map((s) => (
                  <Link key={s.id} to={`/students/${s.id}`}
                    className="flex items-center justify-between py-1.5 text-sm border-b last:border-0 hover:bg-slate-50 px-2 rounded">
                    <span className="text-slate-700">{s.name}</span>
                    <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">
                      {s.cancelled} cancellations
                    </Badge>
                  </Link>
                ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alerts" className="grid lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Near Expiration (next 14 days)</CardTitle>
            </CardHeader>
            <CardContent>
              {students.near_expiration.length === 0
                ? <p className="text-sm text-emerald-600">✅ No students expiring soon</p>
                : students.near_expiration.slice(0, 10).map((s) => (
                  <Link key={s.id} to={`/students/${s.id}`}
                    className="flex items-center justify-between py-1.5 text-sm border-b last:border-0 hover:bg-slate-50 px-2 rounded">
                    <span className="text-slate-700">{s.name}</span>
                    <span className="text-xs text-slate-500">{s.validity_date}</span>
                  </Link>
                ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Monitoring Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Link to="/admin/untagged-alerts" className="flex justify-between py-1.5 border-b hover:bg-slate-50 px-2 rounded">
                <span>Untagged Classes</span>
                <Badge variant="outline" className={alerts.untagged_classes > 0 ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}>
                  {alerts.untagged_classes}
                </Badge>
              </Link>
              <Link to="/admin/schedule-repair" className="flex justify-between py-1.5 border-b hover:bg-slate-50 px-2 rounded">
                <span>Tutors Needing Tagging</span>
                <Badge variant="outline" className={alerts.tutors_with_untagged_count > 0 ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}>
                  {alerts.tutors_with_untagged_count}
                </Badge>
              </Link>
              <Link to="/trials" className="flex justify-between py-1.5 border-b hover:bg-slate-50 px-2 rounded">
                <span>Trial Bookings This Month</span>
                <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                  {alerts.trial_bookings_this_month}
                </Badge>
              </Link>
              <Link to="/students" className="flex justify-between py-1.5 hover:bg-slate-50 px-2 rounded">
                <span>Low-Credit Students</span>
                <Badge variant="outline" className={alerts.low_credit_count > 0 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}>
                  {alerts.low_credit_count}
                </Badge>
              </Link>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
